import React, { useContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Theme,
  TableWithDrawer,
  StickyTableTitle,
  NakedButton,
  DataTableNaked,
  AlertService,
  UnawardIcon,
  Column,
  RowAction,
  ColumnFormatters,
  InformationIconSmall,
  Tooltip,
  SparkleGradientIcon,
  AwardIcon,
  ColorBadge,
  MarketingMountainIcon,
  AlertTriangleIcon,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import { useMutation } from '@apollo/client';
import accounting from 'accounting';
import OfferListings from '../offer-comparison/offer-listings';
import { getColumnsFromDataTableProfile } from '../../components/data-table';
import { DesirabilityIndicator, insertColumnBefore } from '../../components/desirability';
import { weightWithUnit } from '../../utilities/unit-helper';
import {
  TrucklaneDrilldownAwardedOffersZeroState,
  TrucklaneDrilldownOtherOffersZeroState,
  TrucklaneDrilldownSuggestedOffersZeroState,
} from './trucklane-zero-state';
import {
  UnawardOfferListingsByTruckLane,
  UnawardOfferListing,
  AwardSuggestedOfferListings,
  AcceptShiftedOfferListings,
  AwardReservePriceExceptions,
} from '../../graphql/mutations';
import { OfferComparisonStrings } from '../../string-resources';
import { Store } from '../../store';
import {
  ActiveOfferShiftsQuery,
  AwardSummaryQuery,
  trucklanePollingQuery,
  OfferListingsByTrucklaneQuery,
  trucklaneExceptionsQuery,
} from '../../graphql/queries';
import { applicableBuyerMinimums, TrucklaneActionInProgressContext } from './trucklane-utility';
import { formatShiftsForAcceptance } from '../offer-comparison/offer-shift-context';
import { checkFeature } from '../../helpers';
import { featureFlags } from '../../enums';
import { ACTIVE_STEP } from '../offer-comparison/active-step-enums';
import Tracking from '../../utilities/tracking-apis';

const styles = {
  row: {
    display: 'flex',
  },
  tableContainer: {
    marginTop: 5,
  },
  tableBorder: {
    marginBottom: 25,
    borderLeft: `1px solid ${Theme.borderColor}`,
    borderTop: `1px solid ${Theme.borderColor}`,
    borderBottom: `1px solid ${Theme.borderColor}`,
    borderRadius: '2px 0px 0px 2px',
  },
  tableFooter: {
    backgroundColor: `${Theme.blue5} !important`,
    boxShadow: `${Theme.borderColor} 0px 1px inset !important`,
  },
  unawardAll: {
    color: `${Theme.red}`,
    background: 'none',
    fill: `${Theme.red}`,
    '&:hover': {
      border: `1px solid ${Theme.red} !important`,
      cursor: 'pointer',
    },
    '&:active': {
      backgroundColor: `${Theme.red} !important`,
    },
  },
  awardSuggestions: {
    extends: 'unawardAll',
    color: Theme.green,
    border: 'none',
    '& svg': {
      height: 20,
      width: 20,
    },
    '&:hover': {
      '& svg': {
        filter: 'invert(100%) brightness(1000%) contrast(100%)',
      },
    },
  },
  removePadding: {
    padding: '0 !important',
    marginBottom: 25,
  },
  offerCardBorder: {
    borderTop: `1px solid ${Theme.borderColor}`,
    borderLeft: `1px solid ${Theme.borderColor}`,
    borderRight: `1px solid ${Theme.borderColor}`,
  },
  acceptedOfferRow: {
    padding: '8px 0px 8px 12px',
    backgroundColor: Theme.teal5,
    borderBottom: `1px solid ${Theme.tableBorderColor}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& .acceptedOfferTitle': {
      display: 'flex',
      '& svg': {
        fill: Theme.tealDark,
        height: 18,
        width: 18,
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '& p': {
        fontSize: 14,
        fontWeight: 500,
        color: Theme.tealDark,
        margin: 0,
        paddingLeft: 8,
      },
    },
    '& .acceptedMetrics': {
      padding: '8px 24px',
      display: 'flex',
      flexDirection: 'column',
      color: Theme.tealDark,
      fontSize: 14,
      fontWeight: 'bold',
      '& p': {
        fontSize: 12,
        fontWeight: 500,
        textTransform: 'uppercase',
        color: Theme.teal80,
        margin: 0,
      },
    },
  },
  buyerMinimumsRow: {
    background: Theme.orange10,
    borderBottom: `1px solid ${Theme.tableBorderColor}`,
    padding: '12px 12px 12px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    color: Theme.orangeDark,
    '& svg': {
      width: 18,
      height: 18,
      stroke: Theme.orangeDark,
      flexShrink: 0,
    },
    '& span': {
      fontSize: 12,
      fontWeight: 500,
    },
  },
  tooltip: {
    maxWidth: 250,
    fontSize: 14,
    textAlign: 'left',
    padding: 8,
  },
};
const useStyles = createUseStyles(styles);

export const buildDesirabilityColumn = () =>
  new Column({
    field: 'desirability',
    displayName: '',
    defaultSort: false,
    sortable: false,
    formatter: (_, row) => <DesirabilityIndicator desirability={row?.desirability} surface="trucklane-drilldown" />,
  });

const TrucklaneDrilldownTables = ({
  user,
  history,
  recalculation,
  trucklaneId,
  loading,
  awardedInventory,
  fillupInventory,
  suggestedInventory,
  rpeInventory,
  acceptedInventory,
  aggregateOffer,
  activeStep,
  buyerMinimums,
}) => {
  const classes = useStyles();
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [awardSuggestedOfferLoading, setAwardSuggestedOfferLoading] = useState(false);
  const [loadingRows, setLoadingRows] = useState([]);
  const [unawardOfferListingsByTruckLane, { loading: unawardOfferListingsByTruckLaneLoading }] = useMutation(UnawardOfferListingsByTruckLane);
  const [unawardOfferListingById, { loading: unawardOfferListingByIdLoading }] = useMutation(UnawardOfferListing);
  const [awardOfferListingById, { loading: awardOfferListingByIdLoading }] = useMutation(AwardSuggestedOfferListings);
  const [acceptOfferShifts, { loading: acceptingOfferShiftLoading }] = useMutation(AcceptShiftedOfferListings);
  const [awardReservePriceExceptionsMutation, { loading: awardingExceptionsLoading }] = useMutation(AwardReservePriceExceptions);
  const { setActionInProgress } = useContext(TrucklaneActionInProgressContext);
  const reservePriceExceptionEnabled = checkFeature(featureFlags.reservePriceExceptions);
  const OfferListingsByTrucklaneRefetchQuery = {
    query: OfferListingsByTrucklaneQuery,
    variables: {
      buyerSiteId: aggregateOffer?.buyerSiteId || '',
      truckType: aggregateOffer?.truckType || '',
      logisticsTerm: aggregateOffer?.logisticsTerm || '',
      transactionLocationId: aggregateOffer?.transactionLocationId || '',
      sellerLocationId: aggregateOffer?.sellerLocationId || '',
      buyerDestinationId: aggregateOffer?.buyerDestinationId || '',
    },
  };

  const suggestedWeight = useMemo(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return {
        tableFooter: (rows) =>
          weightWithUnit(
            rows.reduce((sum, row) => sum + row.offerListing.suggestions?.award?.quantity * row.unitGrossWeight, 0),
            user,
          ),
        sortField: (row) => row.offerListing.suggestions?.award?.quantity * row.unitGrossWeight,
      };
    }
    return {
      formatter: (_, row) => weightWithUnit(row.offerListing.suggestions?.reservePriceException?.quantity * row.unitGrossWeight, user),
      tableFooter: (rows) =>
        weightWithUnit(
          rows.reduce((sum, row) => sum + row.offerListing.suggestions?.reservePriceException?.quantity * row.unitGrossWeight, 0),
          user,
        ),
      sortField: (row) => row.offerListing.suggestions?.reservePriceException?.quantity * row.unitGrossWeight,
    };
  }, [activeStep, user]);

  const suggestedPrice = useMemo(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return {
        tableFooter: (rows) =>
          accounting.formatMoney(
            rows.reduce(
              (sum, row) =>
                sum +
                row.offerListing.suggestions?.award?.quantity *
                  (row.offerListing.quantity ? row.offerListing.totalPrice / row.offerListing.quantity : 0),
              0,
            ),
          ),
        sortField: (row) => row.offerListing.suggestions?.award?.quantity * row.unitPrice,
      };
    }
    return {
      formatter: (_, row) =>
        accounting.formatMoney(
          row.offerListing.suggestions?.reservePriceException?.quantity *
            (row.offerListing.quantity ? row.offerListing.totalPrice / row.offerListing.quantity : 0),
        ),
      tableFooter: (rows) =>
        accounting.formatMoney(
          rows.reduce(
            (sum, row) =>
              sum +
              row.offerListing.suggestions?.reservePriceException?.quantity *
                (row.offerListing.quantity ? row.offerListing.totalPrice / row.offerListing.quantity : 0),
            0,
          ),
        ),
      sortField: (row) => row.offerListing.suggestions?.reservePriceException?.quantity * row.unitPrice,
    };
  }, [activeStep]);

  const suggestedQuantity = useMemo(() => {
    if (activeStep === ACTIVE_STEP.SUGGESTIONS) {
      return {
        tableFooter: (rows) => rows.reduce((sum, row) => sum + row.offerListing.suggestions?.award?.quantity, 0),
        sortField: (row) => row.offerListing.suggestions?.award?.quantity,
      };
    }
    return {
      formatter: (_, row) => row.offerListing.suggestions?.reservePriceException?.quantity,
      tableFooter: (rows) => rows.reduce((sum, row) => sum + row.offerListing.suggestions?.reservePriceException?.quantity, 0),
      sortField: (row) => row.offerListing.suggestions?.reservePriceException?.quantity,
    };
  }, [activeStep]);

  const refetchQueries = [
    {
      query: trucklanePollingQuery,
      variables: {
        pollingDetails: [...Store.trucklaneChannels.values()].map((channel) => ({ origin: channel.originDCId, truckType: channel.truckType })),
      },
    },
    {
      query: AwardSummaryQuery,
    },
    OfferListingsByTrucklaneRefetchQuery,
  ];
  const columnOverrides = {
    offerListing_quantity: {
      tableFooter: (rows, col) => {
        const isCubeAdjustedWeight = col.formatterString?.includes('totalCubeAdjustedWeight');
        const sum = rows.reduce((acc, row) => acc + (isCubeAdjustedWeight ? row.totalCubeAdjustedWeight : row.offerListing.quantity), 0);
        return isCubeAdjustedWeight ? weightWithUnit(sum, user) : sum;
      },
      sortField: 'offerListing.quantity',
    },
    unitCost: {
      tableFooter: (rows) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.unitCost, 0)),
    },
    weight_quantity: {
      tableFooter: (rows) =>
        weightWithUnit(
          rows.reduce((sum, row) => sum + row.unitGrossWeight * row.offerListing.quantity, 0),
          user,
        ),
      sortField: (row) => row.unitGrossWeight * row.offerListing.quantity,
    },
    totalWeight_offerListing_suggestions_award_quantity: suggestedWeight,
    netWeight_quantity: {
      tableFooter: (rows) =>
        weightWithUnit(
          rows.reduce((sum, row) => sum + row.unitNetWeight * row.offerListing.quantity, 0),
          user,
        ),
      sortField: (row) => row.unitNetWeight * row.offerListing.quantity,
    },
    cubeAdjustedWeight: {
      tableFooter: (rows) =>
        accounting.toFixed(
          rows.reduce((sum, row) => sum + row.cubeAdjustedWeight, 0),
          2,
        ),
    },
    offerListing_totalPrice: {
      tableFooter: (rows) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.offerListing.totalPrice, 0)),
      sortField: 'offerListing.totalPrice',
    },
    total_suggested_price: suggestedPrice,
    unitPrice: {
      tableFooter: (rows) => accounting.formatMoney(rows.reduce((sum, row) => sum + row.unitPrice, 0)),
    },
    activeReservePrice: {
      tableFooter: (rows) =>
        accounting.formatMoney(rows.reduce((sum, row) => sum + ColumnFormatters.generic.access.activeReservePrice(null, row), 0)),
    },
    totalPallets: {
      tableFooter: (rows) =>
        accounting.toFixed(
          rows.reduce((sum, row) => sum + row.totalPallets, 0),
          2,
        ),
    },
    palletsPerOfferListing: {
      tableFooter: (rows) =>
        accounting.toFixed(
          rows.reduce((sum, row) => sum + (row.offerListing.quantity / row.casesPerPallet || 0), 0),
          2,
        ),
    },
    offerPricePerPound: {
      sortField: (row) => {
        const price = row.offerListing.totalPrice / (row.unitNetWeight * row.offerListing.quantity);
        return Number.isFinite(price) ? price : 0;
      },
    },
    offerListing_discountRate: {
      tableFooter: (rows) => {
        const totalPrice = rows.reduce((sum, row) => sum + row.offerListing.totalPrice, 0);
        const awardedInventoryPrice = rows.reduce((sum, row) => sum + row.offerListing.quantity * row.unitPrice, 0);
        return `${accounting.formatNumber((1 - totalPrice / awardedInventoryPrice) * 100 || 0, 2)}%`;
      },
      sortField: 'offerListing.discountRate',
    },
    offerListing_unitPriceRecoveryRate: {
      tableFooter: (rows) => {
        if (rows.length === 0) return '0.00%';

        const totalPrice = rows.reduce((sum, row) => sum + row.offerListing.totalPrice, 0);
        const awardedInventoryPrice = rows.reduce((sum, row) => sum + row.offerListing.quantity * row.unitPrice, 0);

        if (awardedInventoryPrice === 0) return '0.00%';

        const unitPriceRecoveryRate = (totalPrice / awardedInventoryPrice) * 100;

        return `${accounting.formatNumber(unitPriceRecoveryRate, 2)}%`;
      },
      sortField: 'offerListing.unitPriceRecoveryRate',
    },
    offerListing_costRecoveryRate: {
      tableFooter: (rows) => {
        const totalPrice = rows.reduce((sum, row) => sum + row.offerListing.totalPrice, 0);
        const costOfAwardedInv = rows.reduce((sum, row) => sum + row.offerListing.quantity * row.unitCost, 0);
        return `${accounting.formatNumber((totalPrice / costOfAwardedInv) * 100 || 0, 2)}%`;
      },
      sortField: 'offerListing.costRecoveryRate',
    },
    available_cases: {
      tableFooter: (rows) =>
        rows.reduce((sum, row) => {
          const total = sum.split('/');
          const rowCase = ColumnFormatters.generic.access.casesRemaining(null, row).split('/');
          return `${Number(total[0]) + Number(rowCase[0])} / ${Number(total[1]) + Number(rowCase[1])}`;
        }, '0/0'),
      sortField: 'availableQuantity',
    },
    offerListing_suggested_award_quantity: suggestedQuantity,
    totalCubeAdjustedWeight: {
      tableFooter: (rows) =>
        accounting.toFixed(
          rows.reduce((sum, row) => sum + row.totalCubeAdjustedWeight, 0),
          2,
        ),
    },
  };

  const lowDesirabilityEnabled = checkFeature(featureFlags.lowDesirabilityFlag);
  const { awardedColumns, fillupColumns, suggestedColumns, rpeColumns } = useMemo(() => {
    const profileColumns = (dataTable, override) => {
      const columns = getColumnsFromDataTableProfile(dataTable, user.site.dataTableProfiles, override ? columnOverrides : {}, true);
      if (activeStep !== ACTIVE_STEP.OPPORTUNITIES || !lowDesirabilityEnabled) return columns;
      // insertColumnBefore copies the array rather than splicing it. getColumnsFromDataTableProfile
      // memoises into a module-level store and hands back the SAME Column instances every call, so
      // mutating here would corrupt the shared cache for every other table on the page.
      return insertColumnBefore(columns, 'description', buildDesirabilityColumn());
    };
    const defaultColumns = (dataTable, override, competingOffers) => [
      ...profileColumns(dataTable, override),
      competingOffers
        ? new Column({
            field: 'competingOffers',
            displayName: '# of Competing Offers',
            defaultSort: false,
            sortable: true,
            formatter: (_, row) => row.competingOffers,
          })
        : [],
      activeStep === ACTIVE_STEP.OPPORTUNITIES && dataTable === 'Trucklane Drilldown Suggestions'
        ? new Column({
            field: 'opportunityStatus',
            displayName: '',
            defaultSort: false,
            formatter: (_, row) => {
              return row?.offerListing?.suggestions?.reservePriceException?.isException ? (
                <ColorBadge theme="orange">Reserve Price Exception</ColorBadge>
              ) : (
                ''
              );
            },
          })
        : [],
    ];

    return {
      awardedColumns: defaultColumns('Offer Review Buyer Trucklane V2', awardedInventory?.length, true),
      fillupColumns: defaultColumns('Offer Review Buyer Trucklane truckfillup', fillupInventory?.length, true),
      suggestedColumns: defaultColumns('Trucklane Drilldown Suggestions', suggestedInventory?.length, true),
      rpeColumns: defaultColumns('Trucklane Drilldown Suggestions', rpeInventory?.length, true),
    };
  }, [user.site.dataTableProfiles, awardedInventory, fillupInventory, suggestedInventory, rpeInventory, activeStep, lowDesirabilityEnabled]);

  const removeLoadingArray = (id) => {
    const index = loadingRows.indexOf(id);
    const arrayDup = [...loadingRows];
    if (index > -1) {
      arrayDup.splice(index, 1);
    }
    setLoadingRows(arrayDup);
  };

  const handleUnawardAllClick = () => {
    if (!unawardOfferListingsByTruckLaneLoading) {
      setActionInProgress(true);
      unawardOfferListingsByTruckLane({
        variables: {
          buyerSiteId: aggregateOffer.buyerSiteId,
          truckType: aggregateOffer.truckType,
          logisticsTerm: aggregateOffer.logisticsTerm,
          transactionLocationId: aggregateOffer.transactionLocationId,
          sellerLocationId: aggregateOffer.sellerLocationId,
        },
        refetchQueries,
      })
        .then((unawardResp) => {
          if (unawardResp.data.unawardOfferListingsByTruckLane.errors.length) throw new Error();
          AlertService.alert({ type: 'success', autoDismiss: true, message: <span>All offers from the truck lane have been unawarded.</span> });
        })
        .catch(() => AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.unawardingError}</span> }))
        .finally(() => {
          setActionInProgress(false);
        });
    }
  };

  const handleUnawardOfferClick = (row, e) => {
    if (!unawardOfferListingByIdLoading) {
      e.stopPropagation();
      setLoadingRows([...loadingRows, row._id]);

      unawardOfferListingById({
        variables: {
          id: row.offerListing._id,
        },
        refetchQueries,
      })
        .then(() => {
          setSelectedInventory(null);
          removeLoadingArray(row._id);
          AlertService.alert({ type: 'success', autoDismiss: true, message: <span>The offer has been unawarded.</span> });
        })
        .catch(() => AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.unawardingError}</span> }))
        .finally(() => {
          setActionInProgress(false);
        });
    }
  };

  const acceptTrucklaneOfferShifts = async (inventoryToAward, _cache) => {
    let cache = _cache;
    const trucklaneOfferShifts = inventoryToAward.filter((inv) => inv.shift).map((inv) => inv.shift);
    if (trucklaneOfferShifts.length > 0) {
      const acceptShiftResult = await acceptOfferShifts({
        variables: {
          offerShifts: formatShiftsForAcceptance(trucklaneOfferShifts),
        },
        refetchQueries: [{ query: ActiveOfferShiftsQuery }, OfferListingsByTrucklaneRefetchQuery],
        update: (cacheResult) => {
          cache = cacheResult;
        },
      });
      if (acceptShiftResult.data?.acceptShiftedOfferListings.errors && acceptShiftResult.data.acceptShiftedOfferListings.errors.length > 0) {
        throw new Error(acceptShiftResult.data.acceptShiftedOfferListings.errors[0].message);
      }
    }
    return cache;
  };

  const awardSuggestedOffers = async (inventoryToAward, _cache) => {
    const offersToAward = inventoryToAward.filter((inv) => !inv.shift);
    if (offersToAward.length === 0) return _cache;
    setAwardSuggestedOfferLoading(true);
    let cache = _cache;
    const awardResp = await awardOfferListingById({
      variables: {
        offerListingIds: offersToAward.map((inv) => inv.offerListing._id),
      },
      refetchQueries,
      update: (cacheResult) => {
        cache = cacheResult;
      },
    });
    setAwardSuggestedOfferLoading(false);
    if (awardResp.data.awardSuggestedOfferListings.errors) throw new Error();
    return cache;
  };

  const handleAwardAllSuggestionsClick = () => {
    if (!awardOfferListingByIdLoading && !awardSuggestedOfferLoading) {
      (async () => {
        try {
          setActionInProgress(true);
          let cache = null;
          cache = await acceptTrucklaneOfferShifts(suggestedInventory, cache);
          cache = await awardSuggestedOffers(suggestedInventory, cache);
          cache.modify({
            id: cache.identify({ id: trucklaneId, __typename: 'Trucklane' }),
            fields: {
              hasSuggestions() {
                return false;
              },
            },
          });
          AlertService.alert({ type: 'success', autoDismiss: true, message: <span>All suggested offers have been awarded.</span> });
        } catch {
          AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> });
        } finally {
          setAwardSuggestedOfferLoading(false);
          setActionInProgress(false);
        }
      })();
    }
  };

  const handleAwardSuggestionsClick = (row, e) => {
    const cache = null;
    if (!awardOfferListingByIdLoading) {
      e.stopPropagation();
      setActionInProgress(true);
      setLoadingRows([...loadingRows, row._id]);

      const promise = row.shift ? acceptTrucklaneOfferShifts([row], cache) : awardSuggestedOffers([row], cache);
      promise
        .then((cache) => {
          setSelectedInventory(null);
          removeLoadingArray(row._id);
          if (!suggestedInventory.length) {
            cache.modify({
              id: cache.identify({ id: trucklaneId, __typename: 'Trucklane' }),
              fields: {
                hasSuggestions() {
                  return false;
                },
              },
            });
          }
          AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Suggested offer has been awarded.</span> });
        })
        .catch(() => AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span> }))
        .finally(() => {
          setActionInProgress(false);
        });
    }
  };

  const handleAwardAllOpportunitiesClick = useCallback(async () => {
    if (rpeInventory.length === 0) return;
    Tracking.Event('Opportunities were awarded in trucklane drilldown table', {
      userId: user._id,
      transactionCycleId: rpeInventory[0]?.offerListing?.transactionCycleId || '',
    });
    setActionInProgress(true);
    let cache = null;
    try {
      const resp = await awardReservePriceExceptionsMutation({
        variables: { offerListingIds: rpeInventory.map((inv) => inv.offerListing._id) },
        refetchQueries: [...refetchQueries],
        update: (cacheResult) => {
          cache = cacheResult;
        },
      });
      if (resp.data?.awardReservePriceExceptions?.errors?.length) throw new Error(resp.data.awardReservePriceExceptions.errors[0].message);
      if (cache) {
        cache.modify({
          id: cache.identify({ id: trucklaneId, __typename: 'Trucklane' }),
          fields: {
            hasSuggestions() {
              return false;
            },
          },
        });
      }
      AlertService.alert({ type: 'success', autoDismiss: true, message: <span>All opportunities have been awarded.</span> });
    } catch {
      AlertService.alert({ type: 'warning', autoDismiss: true, message: <span>{OfferComparisonStrings.awardingError}</span>, dismissDelay: 5000 });
    } finally {
      setActionInProgress(false);
    }
  }, [awardReservePriceExceptionsMutation, rpeInventory, trucklaneId]);

  const handleAwardOpportunityClick = useCallback(
    async (row) => {
      Tracking.Event('Opportunities were awarded in trucklane drilldown table row', {
        userId: user._id,
        transactionCycleId: rpeInventory[0]?.offerListing?.transactionCycleId || '',
      });
      setActionInProgress(true);
      setLoadingRows((rows) => [...rows, row._id]);
      let cache = null;
      try {
        const resp = await awardReservePriceExceptionsMutation({
          variables: { offerListingIds: [row.offerListing._id] },
          refetchQueries: [...refetchQueries, { query: trucklaneExceptionsQuery }],
          update: (cacheResult) => {
            cache = cacheResult;
          },
        });
        if (resp.data?.awardReservePriceExceptions?.errors?.length) throw new Error(resp.data.awardReservePriceExceptions.errors[0].message);
        setSelectedInventory(null);
        setLoadingRows((rows) => rows.filter((id) => id !== row._id));
        AlertService.alert({ type: 'success', autoDismiss: true, message: <span>Reserve price exception offer has been awarded.</span> });
      } catch {
        AlertService.alert({
          type: 'warning',
          autoDismiss: true,
          message: <span>{OfferComparisonStrings.awardingError}</span>,
          dismissDelay: 5000,
        });
        setLoadingRows((rows) => rows.filter((id) => id !== row._id));
      } finally {
        setActionInProgress(false);
      }
    },
    [awardReservePriceExceptionsMutation, rpeInventory, trucklaneId],
  );

  const awardedRowActions = () => {
    if (awardedInventory?.length)
      return [
        <RowAction
          key={1}
          tooltipText="Unaward Offer"
          loadingTooltipText="Unawarding Offer"
          icon={UnawardIcon}
          loadingRows={loadingRows}
          onClick={(row) => handleUnawardOfferClick.bind(this, row)}
          warning
        />,
      ];
    return [];
  };

  const suggestedRowActions = () => {
    if (suggestedInventory?.length && !recalculation && activeStep !== ACTIVE_STEP.OPPORTUNITIES)
      return [
        <RowAction
          key={2}
          tooltipText="Award Suggested Offer"
          loadingTooltipText="Awarding Offer"
          icon={AwardIcon}
          loadingRows={loadingRows}
          onClick={(row) => handleAwardSuggestionsClick.bind(this, row)}
        />,
      ];
    if (rpeInventory?.length && activeStep === ACTIVE_STEP.OPPORTUNITIES)
      return [
        <RowAction
          key={2}
          tooltipText="Award Suggested Opportunity"
          loadingTooltipText="Awarding Opportunity"
          icon={AwardIcon}
          loadingRows={loadingRows}
          onClick={(row) => handleAwardOpportunityClick.bind(this, row)}
        />,
      ];
    return [];
  };

  const handleRowClick = (inventory) => {
    setSelectedInventory(inventory);
  };

  const calculateAcceptedWeight = () => {
    return `${accounting.formatNumber(
      acceptedInventory.reduce((prev, current) => prev + current.unitGrossWeight * current.offerListing.quantity, 0),
    )} lbs`;
  };

  const calculateAcceptedRevenue = () => {
    return `${accounting.formatMoney(acceptedInventory.reduce((prev, current) => prev + current.offerListing.totalPrice, 0))}`;
  };

  const getSuggestionRows = useCallback(() => {
    if (activeStep === ACTIVE_STEP.OPPORTUNITIES) {
      return rpeInventory;
    }
    return suggestedInventory;
  }, [activeStep, rpeInventory, suggestedInventory]);

  const buyerMinimumsGap = useMemo(() => {
    if (!buyerMinimums || activeStep === ACTIVE_STEP.INACTIVE_LANES) return null;
    const { palletMinimum, weightMinimum } = applicableBuyerMinimums(buyerMinimums, user?.site?.trucklaneMinimums, aggregateOffer?.truckType);
    if (!palletMinimum && !weightMinimum) return null;
    const suggestedQuantity = (row) =>
      (activeStep === ACTIVE_STEP.OPPORTUNITIES
        ? row.offerListing.suggestions?.reservePriceException?.quantity
        : row.offerListing.suggestions?.award?.quantity) || 0;
    const onTruck = [...awardedInventory, ...acceptedInventory].map((row) => ({ row, quantity: row.offerListing.quantity }));
    const suggested = recalculation ? [] : getSuggestionRows().map((row) => ({ row, quantity: suggestedQuantity(row) }));
    const total = [...onTruck, ...suggested];
    const pallets = total.reduce((acc, { row, quantity }) => acc + quantity / (row.casesPerPallet || 1), 0);
    const weight = total.reduce((acc, { row, quantity }) => acc + row.unitGrossWeight * quantity, 0);
    const palletGap = palletMinimum ? Math.round(palletMinimum - pallets) : 0;
    const weightGap = weightMinimum ? Math.round(weightMinimum - weight) : 0;
    if (palletGap <= 0 && weightGap <= 0) return null;
    const gaps = [];
    if (weightGap > 0) gaps.push(`${accounting.formatNumber(weightGap)} lbs`);
    if (palletGap > 0) gaps.push(`${accounting.formatNumber(palletGap)} ${palletGap === 1 ? 'pallet' : 'pallets'}`);
    return { gaps };
  }, [
    buyerMinimums,
    user?.site?.trucklaneMinimums,
    aggregateOffer,
    activeStep,
    awardedInventory,
    acceptedInventory,
    getSuggestionRows,
    recalculation,
  ]);

  return (
    <TableWithDrawer
      className={{ container: classes.tableContainer, table: classes.removePadding }}
      table={
        <div>
          <div className={classes.tableBorder}>
            <StickyTableTitle
              title={
                activeStep !== ACTIVE_STEP.OPPORTUNITIES ? (
                  'Suggested for award'
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MarketingMountainIcon /> Opportunities
                  </div>
                )
              }
            >
              <NakedButton
                onClick={activeStep !== ACTIVE_STEP.OPPORTUNITIES ? handleAwardAllSuggestionsClick : handleAwardAllOpportunitiesClick}
                className={classes.awardSuggestions}
                icon={SparkleGradientIcon}
                loading={(awardOfferListingByIdLoading && awardSuggestedOfferLoading) || acceptingOfferShiftLoading || awardingExceptionsLoading}
                disabled={
                  activeStep !== ACTIVE_STEP.OPPORTUNITIES
                    ? loading || awardOfferListingByIdLoading || awardSuggestedOfferLoading || !suggestedInventory.length || recalculation
                    : loading || awardOfferListingByIdLoading || awardSuggestedOfferLoading || !rpeInventory.length || recalculation
                }
                iq
              >
                {activeStep !== ACTIVE_STEP.OPPORTUNITIES ? 'Award All Suggestions' : 'Award All Opportunities'}
              </NakedButton>
            </StickyTableTitle>
            <DataTableNaked
              data={!loading ? getSuggestionRows() : []}
              columns={activeStep !== ACTIVE_STEP.OPPORTUNITIES ? suggestedColumns : rpeColumns}
              loading={loading}
              sticky
              onRowClick={handleRowClick}
              rowActions={suggestedRowActions()}
              className={{ tableFooter: classes.tableFooter }}
              stickyOffset={62}
              stickyFooter={false}
              highlightRowId={selectedInventory?._id}
              noDataMessage={TrucklaneDrilldownSuggestedOffersZeroState(loading, recalculation)}
              cypressTagTable="drilldown-trucklane-suggested-offers-table"
            />
          </div>
          <div className={classes.tableBorder}>
            <StickyTableTitle title={'Currently on this truck lane'}>
              <NakedButton
                onClick={handleUnawardAllClick}
                className={classes.unawardAll}
                icon={UnawardIcon}
                loading={unawardOfferListingsByTruckLaneLoading}
                disabled={loading || unawardOfferListingsByTruckLaneLoading || !awardedInventory.length}
              >
                Unaward All
              </NakedButton>
            </StickyTableTitle>
            {acceptedInventory?.length > 0 && (
              <div className={classes.acceptedOfferRow}>
                <div className="acceptedOfferTitle">
                  <Tooltip
                    small
                    bottom
                    text={
                      <div className={classes.tooltip}>
                        “Currently on this truck lane” factors in previously accepted inventory. <br />
                        <br /> Accepted inventory is no longer actionable on this page but can be viewed on the history screen.
                      </div>
                    }
                  >
                    <InformationIconSmall />
                  </Tooltip>
                  <p>Accepted Inventory</p>
                </div>
                <div className={classes.row}>
                  <div className="acceptedMetrics" data-testid={'accepted-weight'}>
                    <p>Total Accepted Weight</p>
                    {calculateAcceptedWeight()}
                  </div>
                  <div className="acceptedMetrics" data-testid={'accepted-revenue'}>
                    <p>Total Accepted Revenue</p>
                    {calculateAcceptedRevenue()}
                  </div>
                </div>
              </div>
            )}
            {buyerMinimumsGap && (
              <div className={classes.buyerMinimumsRow} data-testid={'buyer-minimums-gap'}>
                <AlertTriangleIcon />
                <span>{`${buyerMinimumsGap.gaps.join(' & ')} below the buyer pickup minimum`}</span>
              </div>
            )}

            <DataTableNaked
              data={!loading ? awardedInventory : []}
              columns={awardedColumns}
              loading={loading}
              sticky
              onRowClick={handleRowClick}
              rowActions={awardedRowActions()}
              className={{ tableFooter: classes.tableFooter }}
              stickyOffset={62}
              stickyFooter={false}
              highlightRowId={selectedInventory?._id}
              noDataMessage={TrucklaneDrilldownAwardedOffersZeroState(loading, acceptedInventory.length > 0, history)}
              cypressTagTable="drilldown-trucklane-awarded-offers-table"
            />
          </div>
          <div className={classes.tableBorder}>
            <StickyTableTitle title="Other offers for this truck lane" />
            <DataTableNaked
              data={!loading ? fillupInventory : []}
              columns={fillupColumns}
              loading={loading}
              sticky
              onRowClick={handleRowClick}
              className={{ tableFooter: classes.tableFooter }}
              stickyOffset={62}
              stickyFooter={false}
              highlightRowId={selectedInventory?._id}
              noDataMessage={TrucklaneDrilldownOtherOffersZeroState(loading)}
              cypressTagTable="drilldown-trucklane-other-offers-table"
            />
          </div>
        </div>
      }
      cardList={
        <div className={classes.offerCardBorder}>
          {trucklaneId && (
            <OfferListings
              user={user}
              inventoryId={selectedInventory?.id}
              description={selectedInventory?.description}
              trucklaneChannel={Store.trucklaneChannels.get(`${trucklaneId.split(':')[0]}-${trucklaneId.split(':')[2]}`)}
              showSuggestions
              recalculationRequired={recalculation}
              additionalRefetchQueries={[OfferListingsByTrucklaneRefetchQuery]}
            />
          )}
        </div>
      }
    />
  );
};

TrucklaneDrilldownTables.propTypes = {
  user: PropTypes.object,
  history: PropTypes.object,
  recalculation: PropTypes.bool,
  trucklaneId: PropTypes.string,
  loading: PropTypes.bool,
  awardedInventory: PropTypes.array,
  fillupInventory: PropTypes.array,
  suggestedInventory: PropTypes.array,
  rpeInventory: PropTypes.array,
  acceptedInventory: PropTypes.array,
  aggregateOffer: PropTypes.object,
  activeStep: PropTypes.string,
  buyerMinimums: PropTypes.object,
};

export default TrucklaneDrilldownTables;
